import type { ThreadType } from '@form-factory/schema';
import { ThreadMode } from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, cn } from '@utima/ui';
import { Info, Loader2 } from 'lucide-react';
import { memo, useEffect, useMemo, useRef } from 'react';

import avatar from '@/assets/images/avatar.jpg';
import { useOnlineStatus } from '@/hooks';

import { InboxChat } from './InboxChat';
import { MessageComposer } from './MessageComposer';
import { OnlineStatus } from './OnlineStatus';

export interface InboxConversationProps {
  thread: ThreadType | undefined;
  isLoading?: boolean;
  onSubmit: (message: string) => void;
  onToggleMetadata?: () => void;
}

export const InboxConversation = memo(
  ({
    thread,
    isLoading,
    onSubmit,
    onToggleMetadata,
  }: InboxConversationProps) => {
    const { t } = useLingui();
    const systemAvatar = thread?.chat?.chatbotConfig?.systemAvatar || avatar;
    const onlineStatus = useOnlineStatus(thread?.lastActiveAt);

    // Track the last message id for scroll to bottom
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const lastMessageId = useMemo(
      () => thread?.messages?.at(-1)?.id,
      [thread?.messages],
    );
    const prevMessageId = useRef(lastMessageId);

    // Only scroll if we have a new message ID
    useEffect(() => {
      if (
        messagesContainerRef.current &&
        lastMessageId &&
        lastMessageId !== prevMessageId.current
      ) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
        prevMessageId.current = lastMessageId;
      }
    }, [lastMessageId]);

    if (isLoading) {
      return (
        <div className='flex h-full flex-col items-center justify-start pt-12'>
          <Loader2 className='size-10 animate-spin text-primary' />
        </div>
      );
    }

    if (!thread) {
      return (
        <div className='flex h-full items-center justify-center text-muted-fg'>
          <Trans>No thread selected</Trans>
        </div>
      );
    }

    const isUserOnline = onlineStatus === 'online';
    const isUserChattingWithAI = isUserOnline && thread.mode === ThreadMode.AI;

    return (
      <div className='relative flex h-full flex-col'>
        <div className='border-b border-gray-200 p-4'>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-lg font-semibold'>
                {thread.title || <Trans>Untitled</Trans>}
              </h2>
              <div className='mt-1 flex items-center gap-2'>
                <OnlineStatus lastActiveAt={thread.lastActiveAt} />
                {isUserChattingWithAI && (
                  <div className='flex items-center gap-1 text-xs text-amber-600'>
                    <Trans>User is chatting with AI</Trans>
                  </div>
                )}
              </div>
            </div>
            <Button
              data-cta='admin-inbox-conversation-toggle-metadata'
              variant='ghost'
              size='icon-sm'
              onClick={onToggleMetadata}
              className='hidden items-center gap-1 sm:flex'
            >
              <Info className='size-5' />
            </Button>
          </div>
        </div>

        <div
          ref={messagesContainerRef}
          className={cn('flex-1 overflow-auto pb-32')}
        >
          <InboxChat
            messages={thread.messages}
            agentAvatar={systemAvatar}
            agentName={thread.chat?.name}
          />
        </div>

        <MessageComposer
          onSubmit={onSubmit}
          disabled={!isUserOnline}
          placeholder={
            isUserOnline
              ? t`User is online, can't send a message...`
              : t`Type your message...`
          }
        />
      </div>
    );
  },
);
